<template>
  <Teleport v-if="mounted" to="#header-breadcrumbs">
    <CBreadcrumb v-bind="{ routes }" disableGoToBack />
  </Teleport>
  <div
    class="flex flex-col md:flex-row items-start lg:items-center mb-6 gap-4 md:gap-6 justify-between p-4 md:px-5 md:pt-4 md:pb-5 bg-white rounded-xl"
  >
    <h3 class="text-xl font-semibold">{{ $t("edit") }}</h3>
    <div
      class="flex-y-center justify-between md:justify-end w-full gap-2 md:gap-4"
    >
      <RouterLink
        to="/settings?profile=profile"
        class="w-1/2 md:w-auto flex-shrink-0"
      >
        <CButton
          :text="$t('cancel')"
          class="h-10 flex-center text-xs"
          variant="info"
          size="xl"
        />
      </RouterLink>
      <CButton
        :text="$t('save')"
        class="w-1/2 md:w-auto h-10 flex-center text-xs"
        variant="success"
        size="xl"
        :loading="buttonLoading"
        @click="onUpdate"
        :disabled="!isFormChanged"
      />
    </div>
  </div>
  <div class="bg-white rounded-xl p-4 md:p-6">
    <template v-if="!isLoading">
      <div class="flex items-center gap-4 mb-6">
        <div class="flex-y-center gap-2">
          <CProfileAvatar @change="form.values.avatar = $event" />
        </div>
      </div>
      <form
        @submit.prevent
        class="border-none grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-5 lg:py-5"
      >
        <FGroup :label="t('form.full_name')" for-text="name">
          <FInput
            id="name"
            class="!h-10 md:!h-12"
            v-model="form.values.full_name"
            :placeholder="t('form.full_name')"
          />
        </FGroup>
        <FGroup :label="t('form.region')">
          <CountrySelect
            trigger-class="!h-10 md:!h-12"
            :countries="country"
            v-model:country="form.values.country"
            v-model:search="search"
            @handleLoadMoreCountry="onLoadCountries"
          />
        </FGroup>
        <FGroup :label="$t('form.gender')">
          <div class="flex flex-col lg:flex-row items-start gap-4 lg:gap-2">
            <FRadio
              class="!h-10 md:!h-12 p-2 pl-3 bg-gray-100 rounded-lg w-full"
              v-model="form.values.gender"
              name="male"
              value="male"
              :label="$t('male')"
            />
            <FRadio
              class="!h-10 md:!h-12 p-2 pl-3 bg-gray-100 rounded-lg w-full"
              v-model="form.values.gender"
              name="female"
              value="female"
              :label="$t('female')"
            />
          </div>
        </FGroup>
        <div class="grid gap-4 grid-cols-1 lg:grid-cols-2">
          <FGroup :label="t('form.email')" for-text="email">
            <FInput
              id="email"
              v-model="form.values.email"
              :placeholder="user?.email"
              class="flex flex-row-reverse !h-10 md:!h-12"
              input-class="cursor-not-allowed"
              :disabled="form.values?.email"
            >
              <template #prefix>
                <span
                  @click="isShow = true"
                  class="icon-edit text-gray hover:text-gray-700 transition-300 text-xl cursor-pointer"
                ></span>
              </template>
            </FInput>
          </FGroup>
          <FGroup :label="t('form.phone')" for-text="phone">
            <FInputPhone
              class="!h-10 md:!h-12"
              v-model="form.values.phone_number"
              placeholder="+99800 000 00 00"
              maxlength="17"
            />
          </FGroup>
        </div>
      </form>
    </template>
    <div v-else class="w-full min-h-[302px] pb-5 flex flex-col gap-6">
      <div class="flex items-center gap-6">
        <CPreloader
          border-radius="100px"
          class="w-[84px]"
          width="100%"
          height="84px"
          loading
        />
        <div class="flex-y-center flex-col gap-2">
          <CPreloader class="w-full" width="100%" height="36px" loading />
          <CPreloader class="w-full" width="100%" height="36px" loading />
        </div>
      </div>
      <div class="flex items-center justify-center px-4 py-4 flex-col gap-5">
        <div class="flex w-full gap-4">
          <div class="flex items-start w-full flex-col gap-2">
            <CPreloader class="w-[30%]" width="100%" height="18px" loading />
            <CPreloader class="w-full" width="100%" height="40px" loading />
          </div>
          <div class="flex items-start w-full flex-col gap-2">
            <CPreloader class="w-[30%]" width="100%" height="18px" loading />
            <CPreloader class="w-full" width="100%" height="40px" loading />
          </div>
        </div>
        <div class="flex w-full gap-4">
          <div class="flex items-start w-full flex-col gap-2">
            <CPreloader class="w-[30%]" width="100%" height="18px" loading />
            <div class="w-full flex items-center gap-4">
              <CPreloader class="w-full" width="100%" height="40px" loading />
              <CPreloader class="w-full" width="100%" height="40px" loading />
            </div>
          </div>
          <div class="flex items-start w-full flex-col gap-2">
            <CPreloader class="w-[30%]" width="100%" height="18px" loading />
            <div class="w-full flex items-center gap-4">
              <CPreloader class="w-full" width="100%" height="40px" loading />
              <CPreloader class="w-full" width="100%" height="40px" loading />
            </div>
          </div>
        </div>
      </div>
    </div>
    <CDialog
      no-header
      has-close-icon
      :show="isShow"
      @close="closeDialog"
      close-icon-class="text-gray-400"
      :body-class="step === 3 ? 'max-w-[375px] p-6' : '!max-w-full w-full p-6'"
    >
      <CEnterPassword v-if="step === 1" @submit="step = 2" />
      <CEnterEmail
        @on-block="step = 4"
        :email="form.values.email"
        v-if="step === 2"
        @submit="submitEmail"
      />
      <UserEmailChangeConfirm
        v-if="step === 3"
        v-bind="{ email: form.values?.email, time }"
        @submit="isShow = false"
        @on-block="step = 4"
        @on-resend="resendCode"
      />
      <SStepBlocked v-if="step === 4" />
    </CDialog>
  </div>
</template>

<script setup lang="ts">
import { useHandleError } from "@/composables/useHandleError";
import CBreadcrumb from "@/components/Common/CBreadcrumb.vue";
import { useI18n } from "vue-i18n";
import { useMounted } from "@/composables/useMounted";
import { computed, onMounted, ref, watch } from "vue";
import CButton from "@/components/Common/CButton.vue";
import { useAuthStore } from "@/modules/Auth/stores";
import { useForm } from "@/composables/useForm";
import FGroup from "@/components/Form/FGroup.vue";
import FInput from "@/components/Form/Input/FInput.vue";
import FSelect from "@/components/Form/Select/FSelect.vue";
import FRadio from "@/components/Form/Radio/FRadio.vue";
import FInputPhone from "@/components/Form/Input/FInputPhone.vue";
import ApiService from "@/services/ApiService";
import { useCustomToast } from "@/composables/useCustomToast";
import CProfileAvatar from "@/components/CProfileAvatar.vue";
import router from "@/router";
import CPreloader from "@/components/CPreloader.vue";
import CDialog from "@/components/Common/Dialog/CDialog.vue";
import UserEmailChangeConfirm from "@/modules/Profile/components/CUserEmailChangeConfirm.vue";
import SStepBlocked from "@/modules/Auth/components/SStepBlocked.vue";
import CEnterPassword from "@/modules/Profile/components/CEnterPassword.vue";
import CEnterEmail from "@/modules/Profile/components/CEnterEmail.vue";
import { required, email } from "@vuelidate/validators";
import { debounce } from "@/utils";
import CountrySelect from "@/modules/Auth/components/CountrySelect.vue";
import { useLocalizedNavigation } from "@/composables/useLocalizedNavigation";

const { showToast } = useCustomToast();

const { t } = useI18n();
const { mounted } = useMounted();
const authStore = useAuthStore();
const user = computed(() => authStore?.user);
const store = useAuthStore();
const { handleError } = useHandleError();
const isLoading = ref(true);
const loading = ref(false);
const isShow = ref(false);
defineEmits(["back"]);

const isFocused = ref(false);
const search = ref("");
const step = ref(1);
const time = ref(0);

const countriesCount = ref(2);
const currentPage = ref(1);

const buttonLoading = ref(false);

const routes = computed(() => [
  {
    name: t("settings"),
    route: "/settings",
  },
]);
interface ICountry {
  id: number;
  created_at: string;
  updated_at: string;
  title: string;
}
const country = ref<ICountry[]>([]);

const form = useForm(
  {
    avatar: "",
    full_name: "",
    country: "",
    gender: "",
    email: "",
    phone_number: "",
  },
  {
    email: { required, email },
  },
);

const { navigateTo } = useLocalizedNavigation();

const onUpdate = () => {
  form.$v.value.$touch();
  if (!form.$v.value.$invalid) {
    buttonLoading.value = true;

    form.values.phone_number = form.values.phone_number.replace(/\s/g, "");
    const formData = new FormData();

    if (form.values.avatar === "") {
      formData.append("avatar", form.values.avatar);
    }

    if (form.values.avatar instanceof File) {
      formData.append("avatar", form.values.avatar, form.values.avatar.name);
    }

    formData.append("full_name", form.values.full_name);
    formData.append("gender", form.values.gender);
    formData.append("email", form.values.email);
    formData.append("phone_number", form.values.phone_number);
    formData.append("country", form.values.country);

    ApiService.patch("/user/profile/", formData)
      .then(() => {
        authStore.fetchUserData().finally(() => {
          showToast(t("successfully_edited"), "success");
          buttonLoading.value = false;
          navigateTo("/settings?profile=profile");
        });
      })
      .catch(({ response }) => {
        handleError(response);
        buttonLoading.value = false;
      });
  }
};
function closeSelect() {
  isFocused.value = true;
  search.value = "";
}
function getCountry(search?: string, id?: number) {
  loading.value = true;
  ApiService.query("/user/common/country/", {
    params: {
      page: currentPage.value,
      search,
      id,
    },
  })
    .then((res) => {
      if (search || id) {
        country.value = res?.data?.results;
      } else {
        country.value = [...country.value, ...res.data.results];
      }
      // country.value = [...country.value, ...res.data.results];
      countriesCount.value = res?.data?.total_pages;

      form.values.full_name = user.value?.full_name;
      form.values.avatar = user.value?.avatar_url;
      form.values.email = user.value?.email;
      form.values.country = user.value?.country;
      form.values.gender = user.value?.gender;
      form.values.phone_number = user.value?.phone_number;
    })
    .catch((response) => {
      handleError(response);
    })
    .finally(() => (loading.value = false));
}
watch(
  () => search.value,
  (val) => {
    debounce("search", () => getCountry(val));
  },
);

function onLoadCountries() {
  if (currentPage.value < countriesCount.value) {
    currentPage.value++;
    getCountry();
  }
}

onMounted(() => {
  getCountry("", user.value?.country);
  getCountry();
});

const closeDialog = () => {
  isShow.value = false;
  form.values.email = user.value?.email;
  step.value = 1;
};
closeDialog();

async function resendCode() {
  try {
    await store.verifyOtpUserChange(form.values.email?.toLowerCase());
  } catch (err) {
    console.log(err);
  }
}

watch(
  () => isLoading.value,
  () => {
    Promise.resolve().then(() => {
      setTimeout(() => {
        isLoading.value = false;
      }, 800);
      form.values.phone_number = user.value?.phone_number;
    });
  },
  {
    immediate: true,
  },
);
function submitEmail(email: string) {
  step.value = 3;
  form.values.email = email;
}

const isFormChanged = ref(false);

watch(
  () => form.values.avatar,
  (val) => {
    isFormChanged.value = val !== user.value?.avatar_url;
  },
  {
    deep: true,
  },
);

watch(
  () => form.values.phone_number,
  (val) => {
    isFormChanged.value = val?.replaceAll(" ", "") !== user.value?.phone_number;
  },
  {
    deep: true,
  },
);

watch(
  () => form.values.full_name,
  (val) => {
    isFormChanged.value = val !== user.value?.full_name;
  },
  {
    deep: true,
  },
);

watch(
  () => form.values.country,
  (val) => {
    isFormChanged.value = val !== user.value?.country;
  },
  {
    deep: true,
  },
);

watch(
  () => form.values.gender,
  (val) => {
    isFormChanged.value = val !== user.value?.gender;
  },
  {
    deep: true,
  },
);

watch(
  () => form.values.email,
  (val) => {
    isFormChanged.value = val !== user.value?.email;
  },
  {
    deep: true,
  },
);
</script>
